/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import _ from 'underscore';
import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import cx from 'classnames';
import Router from 'next/router';

import Animation from 'components/Animation';
import Button from 'components/Button';
import Container from 'components/Container';
import Icon from 'components/Icon';
import { BodyOverflowContext } from 'contexts/BodyOverflowContext';
import Navigation from 'components/Navigation';

import UsersPropTypes from 'utils/propTypes/users';
import { navigationLinks } from 'utils/helpers/navigation';

import styles from './styles.module.scss';

const userListOptions = [
	{
		title: 'Профиль',
		url: '/profile'
	},
	{
		title: 'Выйти',
		url: '/auth/logout'
	}
];

const MobileMenu = ({ canShow, currentUser, rubricPath, parentTabIndex }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [activeList, setActiveList] = useState(null);
	const [mobileMenuTabIndex, setMobileMenuTabIndex] = useState(-1);
	const menuButtonCloseRef = useRef(null);
	const profileButtonRef = useRef(null);

	const [, setBodyOverflowIsHidden] = useContext(BodyOverflowContext);

	useEffect(() => {
		setBodyOverflowIsHidden('mobile-menu', isOpen && canShow);

		if (isOpen && canShow) {
			setActiveList(null);

			setMobileMenuTabIndex(2);
			menuButtonCloseRef?.current?.focus();
		} else {
			setMobileMenuTabIndex(-1);
		}
	}, [isOpen, canShow, profileButtonRef]);

	const closeMobileMenu = useCallback(() => setIsOpen(false), []);

	useEffect(() => {
		Router.events.on('routeChangeComplete', closeMobileMenu);

		return () => {
			setBodyOverflowIsHidden('mobile-menu', false);
		};
	}, []);

	useEffect(() => {
		const closeMobileMenuOnKeyDown = (event) => {
			if (event.keyCode === 27) {
				setIsOpen(false);
				setMobileMenuTabIndex(-1);
			}
		};

		window.addEventListener('keydown', closeMobileMenuOnKeyDown);

		return () =>
			window.removeEventListener('keydown', closeMobileMenuOnKeyDown);
	}, []);

	const setActiveFirstList = useCallback(() => setActiveList(0), []);
	const setActiveSecondList = useCallback(() => setActiveList(1), []);
	const openMobileMenu = useCallback(() => {
		setIsOpen(true);
		setActiveList(null);
	}, []);

	const renderMobileNavigation = () => {
		const getListCx = (indexList) => {
			const type = indexList === 0 ? 'primary' : 'secondary';

			return cx(styles.list, styles[type], {
				[styles.slideIn]: _.isNumber(activeList) && activeList === indexList,
				[styles.slideOut]: _.isNumber(activeList) && activeList !== indexList
			});
		};

		return ReactDOM.createPortal(
			<Animation show={isOpen && canShow}>
				<div className={styles.menu}>
					<div className={styles.container}>
						<Container className={getListCx(0)}>
							{currentUser ? (
								<div className={styles.item} onClick={setActiveSecondList}>
									<button
										className={styles.button}
										type="button"
										tabIndex={mobileMenuTabIndex}
									>
										<Icon icon="user" className={styles.user} />
										{currentUser.fullName}
										<Icon
											icon="chevronStrokeRight"
											className={styles.chevronStrokeRight}
										/>
									</button>
								</div>
							) : (
								<div className={styles.item}>
									<a
										className={styles.link}
										href="/authorization/login?returnTo=/live"
										tabIndex={mobileMenuTabIndex}
									>
										Войти
									</a>
								</div>
							)}
							<div className={styles.item}>
								<Navigation
									primacy="primary"
									theme="main"
									data={navigationLinks.primary}
									rubricPath={rubricPath}
									parentTabIndex={mobileMenuTabIndex}
								/>
							</div>
						</Container>
						<Container className={getListCx(1)}>
							<div className={styles.item} onClick={setActiveFirstList}>
								<button className={styles.button} type="button">
									<Icon
										icon="chevronStrokeLeft"
										className={styles.chevronStrokeLeft}
									/>
									Назад
								</button>
							</div>
							<div className={styles.item}>
								<Navigation
									primacy="primary"
									theme="main"
									data={userListOptions}
									parentTabIndex={mobileMenuTabIndex}
								/>
							</div>
						</Container>
					</div>
				</div>
			</Animation>,
			document.querySelector('#portal')
		);
	};

	return (
		<>
			{isOpen ? (
				<Button
					icon="close"
					tooltip="Закрыть меню"
					theme="onlyIcon"
					onClick={closeMobileMenu}
					className={styles.MobileButtonMenu}
					tabIndex={mobileMenuTabIndex === 2 ? 1 : -1}
					ref={menuButtonCloseRef}
				/>
			) : (
				<Button
					icon="nav"
					tooltip="Меню"
					theme="onlyIcon"
					onClick={openMobileMenu}
					tabIndex={parentTabIndex}
					className={styles.MobileButtonMenu}
				/>
			)}
			{renderMobileNavigation()}
		</>
	);
};

MobileMenu.propTypes = {
	canShow: PropTypes.bool,
	rubricPath: PropTypes.string,
	currentUser: UsersPropTypes.user,
	parentTabIndex: PropTypes.number
};

MobileMenu.defaultProps = {
	canShow: true
};

export default MobileMenu;
